import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type { SolicitudDetalle } from "@/lib/types";
import {
  autorizarSolicitud,
  darDeBajaSolicitud,
  rechazarSolicitud,
} from "../services/autorizacion-actions";

/**
 * Las 3 mutations comparten la misma estrategia post-éxito:
 *  · `setQueryData` para hidratar la solicitud al instante (sin esperar
 *    al próximo refetch del polling).
 *  · `invalidateQueries` de la bandeja · puede haber salido (autorizada
 *    terminal o baja) o avanzado de nivel.
 *
 * Los toasts son distintos · el caller los pasa via opciones.
 */
function useHidratarYInvalidar(id: string) {
  const qc = useQueryClient();
  return (sol: SolicitudDetalle) => {
    qc.setQueryData(qk.solicitud(id), sol);
    void qc.invalidateQueries({ queryKey: qk.bandejaAutorizacion });
    // La fecha de pago recién fijada debe reflejarse en "Mis solicitudes" y en el historial.
    void qc.invalidateQueries({ queryKey: qk.misSolicitudes });
    void qc.invalidateQueries({ queryKey: qk.historialAutorizaciones });
  };
}

export function useAutorizar(id: string) {
  const hidratar = useHidratarYInvalidar(id);
  return useMutation({
    mutationFn: (fechaPago?: string | null) => autorizarSolicitud(id, fechaPago),
    onSuccess: (sol) => {
      hidratar(sol);
      toastExito({
        title: sol.estado === "Autorizada" ? "Solicitud autorizada" : "Autorizado",
        message:
          sol.estado === "Autorizada"
            ? "Autorizada en todos los niveles."
            : `Pasa al nivel ${sol.flujo?.nivelActual ?? "?"}`,
      });
    },
    onError: toastErrorApi,
  });
}

export function useRechazar(id: string, opts?: { onRechazado?: () => void }) {
  const hidratar = useHidratarYInvalidar(id);
  return useMutation({
    mutationFn: (motivos: string[]) => rechazarSolicitud(id, motivos),
    onSuccess: (sol) => {
      hidratar(sol);
      toastExito({ title: "Solicitud rechazada", message: "Enviada al solicitante para corrección." });
      opts?.onRechazado?.();
    },
    onError: toastErrorApi,
  });
}

export function useDarDeBaja(id: string, opts?: { onBaja?: () => void }) {
  const hidratar = useHidratarYInvalidar(id);
  return useMutation({
    mutationFn: (motivos: string[]) => darDeBajaSolicitud(id, motivos),
    onSuccess: (sol) => {
      hidratar(sol);
      toastExito({ title: "Solicitud dada de baja", message: "Acción terminal." });
      opts?.onBaja?.();
    },
    onError: toastErrorApi,
  });
}
